require('./cmValidationSummary.scss');

const PropTypes = require('prop-types');

// @vendor
const React = require('react');
const classnames = require('classnames');

// @commons
const { OKButton } = require('components/commons/OKButton/OKButton');
const { OKTable } = require('commonsComponents/OKTable');
const { OKTableRow } = require('commonsComponents/OKTableRow');
const { OKTableCell } = require('commonsComponents/OKTableCell');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');

// @components
const {
    OneLineSymbolAmount,
} = require('commonsComponents/oneLineSymbolAmount');
const CmValidationRowSummary = require('./cmValidationRowSummary/cmValidationRowSummary');

// @helpers
const { FormattedText, getLanguage } = require('core/i18n').i18n;
const { formatAmountObject } = require('utilities/currencyHelper');

// @constants
const {
    CM_ACTIVE_CLASS_MONEY_MARKET,
    CM_ACTIVE_CLASS_FIXED_INCOME,
    CM_ACTIVE_CLASS_EQUITY,
    CM_ACTIVE_CLASS_REAL_ASSETS,
    CM_OPERATION_TYPE_TRASPASS,
    CM_EUR_CURRENCY,
} = require('../../constants/constants');

class CmValidationSummaryTable extends React.Component {
    constructor(props) {
        super(props);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.getRowAmount = this.getRowAmount.bind(this);
    }

    handleDocumentClick(isin) {
        this.props.onLegalDocumentClick(isin);
    }

    sumAmounts(assets) {
        const { success } = this.props;
        let totalAmount = 0;
        const currency = CM_EUR_CURRENCY;

        assets.forEach(asset => {
            if (asset.get('destinationFundData')) {
                totalAmount += asset.getIn([
                    'destinationFundData',
                    'subAssetClassFundAmount',
                ]);
            } else if (
                success &&
                asset
                    .get('operationType')
                    .startsWith(CM_OPERATION_TYPE_TRASPASS)
            ) {
                totalAmount += Number.parseFloat(
                    asset.getIn(['fondoDestino', 'precioTotalDestino']),
                );
            } else {
                totalAmount += asset.get('subAssetClassFundAmount');
            }
        });

        return {
            amount: totalAmount,
            currency,
        };
    }

    getRowClass(assetClass) {
        const customClass = classnames('cm-funds-summary__row', {
            ['cm-funds-summary__row--money-market']:
                assetClass === CM_ACTIVE_CLASS_MONEY_MARKET,
            ['cm-funds-summary__row--fixed-income']:
                assetClass === CM_ACTIVE_CLASS_FIXED_INCOME,
            ['cm-funds-summary__row--equity']:
                assetClass === CM_ACTIVE_CLASS_EQUITY,
            ['cm-funds-summary__row--real-assets']:
                assetClass === CM_ACTIVE_CLASS_REAL_ASSETS,
        });
        return customClass;
    }

    getRowAmount(subAssetClassFundAmount) {
        return {
            amount: subAssetClassFundAmount,
            currency: CM_EUR_CURRENCY,
        };
    }

    buildTableHeader() {
        const { isTransferWithMoney } = this.props;
        const showBuyButton = this.props.showBuyButton ? (
            <OKTableCell
                asHeader={true}
                className="header-cell cm-funds-summary__header-cell--last"
            ></OKTableCell>
        ) : null;
        const customClass = classnames({
            'header-cell cm-funds-summary__header-cell--last': !this.props
                .showBuyButton,
            'cm-funds-summary__header-cell cm-funds-summary__header-cell--fourth': this
                .props.showBuyButton,
        });
        const secondHeaderClass = 'cm-funds-summary__header-cell--second';

        let legalDocumentHeader;
        let orderTypeHeader;

        if (isTransferWithMoney) {
            orderTypeHeader = (
                <OKTableCell
                    asHeader={true}
                    className="cm-funds-summary__header-cell cm-funds-summary__header-cell--third-additional"
                >
                    <FormattedText value="stocksContract-orderType" />
                </OKTableCell>
            );
        }

        if (!this.props.hideDocument) {
            legalDocumentHeader = (
                <div>
                    <FormattedText
                        value="brokerCM-fundsSummaryLegalDocumentHeader"
                        className="cm-funds-summary__header-document-content"
                    />
                    <OKTooltip
                        offset={[0, -5]}
                        content="brokerCM-buyStep1Tooltip2"
                    />
                </div>
            );
        }
        const doNotHaveLegalDocumentColumnClassName = classnames(
            'cm-funds-summary__header-cell',
            {
                'cm-funds-summary__header-cell--first':
                    this.props.hideDocument === false,
                'cm-funds-summary__header-cell--hide-legal-column':
                    this.props.hideDocument === true,
            },
        );

        return (
            <OKTableRow
                asHeader={true}
                className="cm-funds-summary__row cm-funds-summary__header-row hidden-xs"
            >
                <OKTableCell
                    asHeader={true}
                    className={doNotHaveLegalDocumentColumnClassName}
                >
                    <FormattedText
                        value="brokerCM-fundsSummaryFundNameHeader"
                        className="cm-funds-summary__header-content"
                    />
                </OKTableCell>
                <OKTableCell
                    asHeader={true}
                    className={
                        'cm-funds-summary__header-cell ' + secondHeaderClass
                    }
                >
                    <FormattedText
                        value="brokerCM-fundsSummaryActiveClassHeader"
                        className="cm-funds-summary__header-content"
                    />
                </OKTableCell>
                <OKTableCell
                    asHeader={true}
                    className="cm-funds-summary__header-cell cm-funds-summary__header-cell--third"
                >
                    {legalDocumentHeader}
                </OKTableCell>
                {orderTypeHeader}
                <OKTableCell asHeader={true} className={customClass}>
                    <FormattedText
                        value="brokerCM-fundsSummaryLegalAmountHeader"
                        className="cm-funds-summary__header-content"
                    />
                </OKTableCell>
                {showBuyButton}
            </OKTableRow>
        );
    }

    buildTableBody(assets) {
        const {
            isFromCalculator,
            hasFundProductLink,
            minAmounts,
            success,
        } = this.props;
        let sortedAssets;
        let getSortingValue;
        if (isFromCalculator) {
            getSortingValue = (imm, key) =>
                Number(imm.get(key) ? imm.get(key).slice(-3) : 0);
            sortedAssets = assets.sort(
                (a, b) =>
                    getSortingValue(a, 'subAssetClassId') -
                    getSortingValue(b, 'subAssetClassId'),
            );
        } else {
            getSortingValue = (imm, key, key2) => {
                if (imm.get('assetClassId')) {
                    return Number(
                        imm.get(key)
                            ? imm
                                  .get(key)
                                  .get(key2)
                                  .slice(-3)
                            : imm.get(key2).slice(-3),
                    );
                }
                return 0;
            };
            sortedAssets = assets.sort(
                (a, b) =>
                    getSortingValue(
                        a,
                        'destinationFundData',
                        'subAssetClassId',
                    ) -
                    getSortingValue(
                        b,
                        'destinationFundData',
                        'subAssetClassId',
                    ),
            );
        }

        return sortedAssets.map((asset, index) => {
            const destinationFundData = asset.get('destinationFundData')
                ? asset.get('destinationFundData')
                : asset;
            const assetClassId = destinationFundData.get('assetClassId');
            const assetClassDesc = destinationFundData.get('assetClassDesc');
            const subAssetClassDesc = destinationFundData.get(
                'subAssetClassDesc',
            );
            const subAssetClassIsin = destinationFundData.get(
                'subAssetClassIsin',
            );
            const subAssetClassFundName = destinationFundData.get(
                'subAssetClassFundName',
            );
            const fundProduct = destinationFundData.get('fundProduct');
            const fundSubproduct = destinationFundData.get('fundSubproduct');
            const orderType = asset.get('operationType');

            let secId, subAssetClassFundAmount;

            if (hasFundProductLink) {
                secId = minAmounts
                    .find(fund => fund.get('isin') === subAssetClassIsin)
                    .get('secId');
            }

            if (success && orderType.startsWith(CM_OPERATION_TYPE_TRASPASS)) {
                subAssetClassFundAmount = destinationFundData.getIn([
                    'fondoDestino',
                    'precioTotalDestino',
                ]);
            } else {
                subAssetClassFundAmount = destinationFundData.get(
                    'subAssetClassFundAmount',
                );
            }

            let legalDocument;
            if (!this.props.hideDocument) {
                legalDocument = (
                    <div className="cm-funds-summary__icon-wrapper">
                        <OKButton
                            modifier="additional-action-secondary cm-funds-summary__cell cm-funds-summary__cell--third-button"
                            size="small"
                            loaderColor="dark"
                            loaderSize="small"
                            onClick={() =>
                                this.handleDocumentClick(subAssetClassIsin)
                            }
                        >
                            <i className="icon-link-externo cm-funds-summary__icon"></i>
                        </OKButton>
                    </div>
                );
            }

            return (
                <CmValidationRowSummary
                    key={index}
                    assetClassId={assetClassId}
                    assetClassDesc={assetClassDesc}
                    subAssetClassDesc={subAssetClassDesc}
                    legalDocument={legalDocument}
                    subAssetClassFundAmount={subAssetClassFundAmount}
                    subAssetClassIsin={subAssetClassIsin}
                    subAssetClassFundName={subAssetClassFundName}
                    fundProduct={fundProduct}
                    fundSubproduct={fundSubproduct}
                    onHandleDocumentClick={this.handleDocumentClick}
                    showBuyButton={this.props.showBuyButton}
                    isTransferWithMoney={this.props.isTransferWithMoney}
                    orderType={orderType}
                    hasFundProductLink={hasFundProductLink}
                    secId={secId}
                />
            );
        });
    }

    buildTableFooter(assets) {
        const { isTransferWithMoney } = this.props;
        let orderTypeCell;

        if (isTransferWithMoney) {
            orderTypeCell = (
                <OKTableCell className="cm-funds-summary__footer-cell hidden-xs"></OKTableCell>
            );
        }

        const totalAmount = this.sumAmounts(assets);
        const showBuyButton = this.props.showBuyButton ? (
            <OKTableCell className="cm-funds-summary__footer-cell hidden-xs"></OKTableCell>
        ) : null;

        return (
            <OKTableRow className="cm-funds-summary__footer-row">
                <OKTableCell className="cm-funds-summary__footer-cell cm-funds-summary__footer-cell--first clearfix">
                    <FormattedText
                        value="brokerCM-fundsSummaryAmountToInvest"
                        className="cm-funds-summary__footer-content-first"
                    />
                    <div className="visible-xs pull-right">
                        <OneLineSymbolAmount
                            amountObject={totalAmount}
                            className="cm-funds-summary__footer-content-last"
                            language={getLanguage()}
                            amountObjectFormatted={formatAmountObject(
                                totalAmount,
                                true,
                            )}
                        />
                    </div>
                </OKTableCell>
                <OKTableCell className="cm-funds-summary__footer-cell hidden-xs"></OKTableCell>
                <OKTableCell className="cm-funds-summary__footer-cell hidden-xs"></OKTableCell>
                {orderTypeCell}
                {showBuyButton}
                <OKTableCell className="cm-funds-summary__footer-cell cm-funds-summary__footer-cell--last hidden-xs">
                    <OneLineSymbolAmount
                        amountObject={totalAmount}
                        className="cm-funds-summary__footer-content-last"
                        language={getLanguage()}
                        amountObjectFormatted={formatAmountObject(
                            totalAmount,
                            true,
                        )}
                    />
                </OKTableCell>
            </OKTableRow>
        );
    }

    render() {
        const { portfolio, hideTotal } = this.props;
        const tableHeader = this.buildTableHeader();
        const tableBody = this.buildTableBody(portfolio);
        const tableFooter = hideTotal ? '' : this.buildTableFooter(portfolio);

        return (
            <OKTable className="cm-funds-summary">
                <thead className="cm-funds-summary__table-header">
                    {tableHeader}
                </thead>
                <tbody>{tableBody}</tbody>
                <tfoot>{tableFooter}</tfoot>
            </OKTable>
        );
    }
}

CmValidationSummaryTable.propTypes = {
    portfolio: PropTypes.object,
    onLegalDocumentClick: PropTypes.func,
    hideTotal: PropTypes.bool,
    hideDocument: PropTypes.bool,
    showGeneralMinAmountMessage: PropTypes.bool,
    isFromCalculator: PropTypes.bool,
    showBuyButton: PropTypes.bool,
    isTransferWithMoney: PropTypes.bool,
    originFunds: PropTypes.object, //calculator funds array response we may filter with validation response in order to get operations type when its twice
    hasFundProductLink: PropTypes.bool,
    minAmounts: PropTypes.object,
    success: PropTypes.bool,
};

CmValidationSummaryTable.defaultProps = {
    isFromCalculator: false,
};

module.exports = CmValidationSummaryTable;
